import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Alert, Spinner, Row, Col, Image, Form } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';
import { baseURL } from '../../services/api';
import * as yup from 'yup';
import { useFormik } from 'formik';

// Utility functions for avatar
const getInitials = (name) => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const getColorFromName = (name) => {
  if (!name) return '#3b82f6';
  const colors = [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#f59e0b', // amber
    '#ef4444', // red
    '#14b8a6', // teal
    '#f97316'  // orange
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const SelfProfile = () => {
  const { token, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [resetStatus, setResetStatus] = useState({ type: '', message: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [changePasswordStatus, setChangePasswordStatus] = useState({ type: '', message: '' });

  const validationSchema = yup.object({
    currentPassword: yup.string().required('Current password is required'),
    newPassword: yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        'Must contain uppercase, lowercase, number and special character'
      )
      .required('New password is required'),
    confirmPassword: yup.string()
      .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
      .required('Please confirm your password')
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setChangePasswordStatus({ type: 'info', message: 'Changing password...' });
        
        await axios.post(
          `${baseURL}api/change-password`,
          {
            current_password: values.currentPassword,
            new_password: values.newPassword,
            new_password_confirmation: values.confirmPassword
          },
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        setChangePasswordStatus({ 
          type: 'success', 
          message: 'Password changed successfully!' 
        });
        resetForm();
      } catch (error) {
        console.error('Password change error:', error);
        const message = error.response?.data?.message || 'Failed to change password';
        setChangePasswordStatus({ 
          type: 'danger', 
          message: message 
        });
      }
    }
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${baseURL}api/me/employee`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(response.data);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token]);

  const handlePasswordReset = async () => {
    try {
      setResetStatus({ type: 'info', message: 'Sending reset link...' });
      await axios.post(
        `${baseURL}api/send-password-reset-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetStatus({ 
        type: 'success', 
        message: 'Password reset link sent to your email' 
      });
    } catch (err) {
      console.error('Password reset error:', err);
      setResetStatus({ 
        type: 'danger', 
        message: 'Failed to send reset link' 
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  const displayProfile = profile || {
    name: user?.name || 'User',
    email: user?.email || '',
    is_superadmin: user?.role === 'superadmin'
  };

  if (displayProfile.is_superadmin) {
    return (
      <Card className="mt-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">Super Admin Dashboard</h5>
        </Card.Header>
        <Card.Body className="text-center">
          <div 
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              backgroundColor: getColorFromName(displayProfile.name),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              fontSize: '2.5rem',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {getInitials(displayProfile.name)}
          </div>
          <h4>{displayProfile.name}</h4>
          <p className="text-muted">{displayProfile.email}</p>
          <p className="text-info">
            <i className="fas fa-shield-alt me-2"></i>
            Super Administrator Access
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-4 shadow-sm">
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0">My Profile</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={4} className="text-center">
            <div 
              style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                fontSize: '3rem',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: profile?.profile_image ? 'transparent' : getColorFromName(displayProfile.name),
                overflow: 'hidden',
                border: '4px solid #f1f5f9'
              }}
            >
              {profile?.profile_image ? (
                <Image 
                  src={`${baseURL}storage/${profile.profile_image}`}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }}
                  alt="Profile"
                />
              ) : (
                getInitials(displayProfile.name)
              )}
            </div>
          </Col>
          <Col md={8}>
            <div className="mb-4">
              <h3>{displayProfile.name}</h3>
              <p className="text-muted mb-4">{displayProfile.email}</p>
              
              <div className="profile-details">
                <div className="d-flex align-items-center mb-3">
                  <i className="fas fa-phone me-3 text-muted"></i>
                  <div>
                    <div className="text-muted small">Phone</div>
                    <div>{profile?.phone || 'Not provided'}</div>
                  </div>
                </div>
                
                {profile?.department?.name && (
                  <div className="d-flex align-items-center mb-3">
                    <i className="fas fa-building me-3 text-muted"></i>
                    <div>
                      <div className="text-muted small">Department</div>
                      <div>{profile.department.name}</div>
                    </div>
                  </div>
                )}
                
                {profile?.designation?.name && (
                  <div className="d-flex align-items-center mb-3">
                    <i className="fas fa-briefcase me-3 text-muted"></i>
                    <div>
                      <div className="text-muted small">Designation</div>
                      <div>{profile.designation.name}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 pt-3 border-top">
              <h5>Change Password</h5>
              <p className="text-muted small mb-3">
                Change your account password. Make sure to use a strong password.
              </p>
              
              <Form onSubmit={formik.handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="currentPassword"
                    value={formik.values.currentPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.currentPassword && !!formik.errors.currentPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.currentPassword}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="newPassword"
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.newPassword && !!formik.errors.newPassword}
                  />
                  <Form.Text className="text-muted">
                    Must be at least 8 characters with uppercase, lowercase, number, and special character
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.newPassword}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.confirmPassword}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <div className="d-flex justify-content-between align-items-center">
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Changing...
                      </>
                    ) : 'Change Password'}
                  </Button>
                  
                  <Button 
                    variant="link" 
                    onClick={handlePasswordReset}
                    disabled={resetStatus.type === 'info'}
                    className="text-decoration-none"
                  >
                    {resetStatus.type === 'info' ? (
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                    ) : (
                      <i className="fas fa-key me-1"></i>
                    )}
                    Forgot Password?
                  </Button>
                </div>
                
                {changePasswordStatus.message && (
                  <Alert 
                    variant={changePasswordStatus.type} 
                    className="mt-3 mb-0"
                  >
                    {changePasswordStatus.message}
                  </Alert>
                )}
                
                {resetStatus.message && (
                  <Alert 
                    variant={resetStatus.type} 
                    className="mt-3 mb-0"
                  >
                    {resetStatus.message}
                  </Alert>
                )}
              </Form>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default SelfProfile;